import type { ReactNode } from 'react';
import { cx } from '~/ui/cx.js';
import {
  ARCH,
  DEFAULT_FINISH,
  FIELD_INSET,
  archLane,
  archPath,
  channelBand,
  fieldLanes,
  holeRadius,
  homeNumeral,
  notches,
  streetNumeral,
  streets,
  type ArchFinish,
  type ArchGeometry,
  type ArchSize,
  type HoleWeight,
  type Spot,
} from './archGeometry.js';

/**
 * The board: the arch, with the playing surface inside it.
 *
 * Graduated from `dev/sketches/classic-kit.tsx`. The pegs are the only marks
 * on it: the front peg solid in the side's color inside an ink line, and the
 * back peg a ring where the side stood before its last scoring event, so the
 * gap between the two is the last score at a glance. Nothing on the board
 * names the skunk; the end game's stamp does.
 *
 * **A peg home wears a ring** in its side's full stop, 4 clear of the peg:
 * the mark a lit card wears at the show, so the peg that won reads as the
 * last thing lit (`classic-show` E). It is drawn for whatever landed the peg
 * there, a count or the play.
 *
 * **The pegs read the paced store and the name reads the view.** A hand's
 * scoring arrives in one burst and the front peg hops item by item after it,
 * so the caller hands this component the paced pegs and the view's own
 * numbers for the accessible name. It is the one image on the table, and it
 * speaks the score, because a picture of a peg track that says only "board"
 * has told a screen reader nothing.
 *
 * A finish changes what a hole is and what runs under the lanes, and nothing
 * else. `edged` is the default (Mitch, 2026-09-16). The player picks one in
 * Settings → Game (`settingsStore.boardFinish`), and `ClassicTable` passes it.
 */

/** Which side a lane belongs to, as its color. */
export type LaneTone = 'teal' | 'tangerine' | 'periwinkle';

/** One lane: whose it is and where their pegs stand. */
export interface ArchLane {
  readonly tone: LaneTone;
  /** The front peg: the score as the board knows it. */
  readonly score: number;
  /** The back peg: where the side stood before its last scoring event. */
  readonly back: number;
}

const FRONT: Readonly<Record<LaneTone, string>> = {
  teal: 'fill-teal stroke-ink',
  tangerine: 'fill-tangerine stroke-ink',
  periwinkle: 'fill-periwinkle stroke-ink',
};

const BACK: Readonly<Record<LaneTone, string>> = {
  teal: 'stroke-teal',
  tangerine: 'stroke-tangerine',
  periwinkle: 'stroke-periwinkle',
};

/** What a channel is painted in: the stroke draws its run, the fill its feet. */
const CHANNEL_PAINT = {
  'gray-300': { stroke: 'stroke-gray-300', fill: 'fill-gray-300' },
  'ground-dark': { stroke: 'stroke-ground-dark', fill: 'fill-ground-dark' },
  'ground-light': { stroke: 'stroke-ground-light', fill: 'fill-ground-light' },
} as const;

type ChannelPaint = keyof typeof CHANNEL_PAINT;

export interface ArchProps {
  readonly size: ArchSize;
  /** One per side, yours first: you outside, the seat opposite inside. */
  readonly lanes: readonly ArchLane[];
  /** The board's accessible name: the view's own scores, said in words. */
  readonly label: string;
  readonly target: number;
  readonly finish?: ArchFinish | undefined;
  /**
   * Replaces the size's height. Three- and four-handed tables ask for a taller
   * arch, because a pile row per lane has to fit inside at the hand card's
   * size. The width, the corner and the lanes do not move.
   */
  readonly height?: number | undefined;
  /**
   * A detail of the board, as a `viewBox` in the arch's own coordinates. The
   * drawing scales to fill its box instead of standing at its true size, so
   * the Settings board picker can show a stretch of one leg without a second
   * copy of the finish code.
   */
  readonly crop?: string | undefined;
  /** The playing surface, inside the arch and open at its foot. */
  readonly children?: ReactNode;
}

export function Arch({
  size,
  lanes,
  label,
  target,
  finish = DEFAULT_FINISH,
  height,
  crop,
  children,
}: ArchProps): React.JSX.Element {
  const s: ArchGeometry = { ...ARCH[size], h: height ?? ARCH[size].h };
  const n = lanes.length;
  const holes = Array.from({ length: target - 1 }, (_, index) => index + 1);
  const labelClass = s.label <= 11 ? 'text-xs' : 'text-sm';
  const home = archLane(s, target, 0, n, target);
  const homeLabel = homeNumeral(s, n, target, finish);

  const channel = (paint: ChannelPaint, extra = 0): React.JSX.Element => {
    const band = channelBand(s, n, target, finish, extra);
    const { stroke, fill } = CHANNEL_PAINT[paint];
    return (
      <g key={paint}>
        <path
          d={band.run}
          fill="none"
          className={stroke}
          strokeWidth={band.width}
          strokeLinecap="butt"
          strokeLinejoin="round"
        />
        <path d={band.stallCap} className={fill} />
        <path d={band.homeCap} className={fill} />
      </g>
    );
  };

  /** What runs under the holes: a hairline per lane, or one channel. */
  const under = (): ReactNode => {
    switch (finish) {
      case 'gray':
        return channel('gray-300');
      case 'drilled':
        return channel('ground-dark');
      case 'edged':
        return [channel('gray-300', 2), channel('ground-dark')];
      case 'strip':
        return [channel('gray-300', 3), channel('ground-light')];
      default:
        return lanes.map((_, index) => (
          <path
            key={`lane-${String(index)}`}
            d={archPath(s, index, n, target)}
            fill="none"
            className="stroke-gray-300"
            strokeWidth={2}
          />
        ));
    }
  };

  /** One hole in the finish's vocabulary. */
  const hole = (key: React.Key, spot: Spot, weight: HoleWeight): React.JSX.Element => {
    const r = holeRadius(s, weight);
    switch (finish) {
      case 'ticks': {
        if (weight === 2) {
          return <circle key={key} cx={spot.x} cy={spot.y} r={r} className="fill-gray-400" />;
        }
        const reach = weight === 0 ? s.dot + 1 : s.dot + 2.5;
        return (
          <line
            key={key}
            x1={spot.x - spot.nx * reach}
            y1={spot.y - spot.ny * reach}
            x2={spot.x + spot.nx * reach}
            y2={spot.y + spot.ny * reach}
            className={weight === 0 ? 'stroke-gray-300' : 'stroke-gray-400'}
            strokeWidth={1.5}
          />
        );
      }
      case 'gray':
      case 'edged':
        return <circle key={key} cx={spot.x} cy={spot.y} r={r} className="fill-ground" />;
      default:
        return <circle key={key} cx={spot.x} cy={spot.y} r={r} className="fill-gray-400" />;
    }
  };

  const notchPaint =
    finish === 'dots' || finish === 'ticks' ? 'stroke-gray-400' : 'stroke-gray-300';

  return (
    <div className={cx('relative', crop !== undefined && 'size-full')}>
      <svg
        {...(crop === undefined ? { width: s.w, height: s.h } : {})}
        viewBox={crop ?? `0 0 ${String(s.w)} ${String(s.h)}`}
        role="img"
        aria-label={label}
        className={
          crop === undefined ? 'block overflow-visible' : 'block size-full overflow-hidden'
        }
        data-finish={finish}
      >
        {under()}
        {streets(target).map((at) => (
          <g key={`notches-${String(at)}`}>
            {notches(s, at, n, target, finish).map((segment, index) => (
              <line
                key={index}
                {...segment}
                className={notchPaint}
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            ))}
          </g>
        ))}
        {lanes.map((_, index) => (
          <g key={`holes-${String(index)}`}>
            {holes.map((h) => hole(h, archLane(s, h, index, n, target), h % 5 === 0 ? 1 : 0))}
          </g>
        ))}
        {streets(target).map((at) => {
          const numeral = streetNumeral(s, at, n, target, finish);
          return (
            <text
              key={`street-${String(at)}`}
              x={numeral.x}
              y={numeral.y}
              textAnchor={numeral.anchor}
              dominantBaseline={numeral.baseline}
              className={cx('fill-gray-700 font-extrabold', labelClass)}
            >
              {at}
            </text>
          );
        })}
        {/* Home: one hole at the foot of the right leg, between the lanes, a
            step heavier than a fifth, its number under it. */}
        <text
          x={homeLabel.x}
          y={homeLabel.y}
          textAnchor="middle"
          className={cx('fill-gray-700 font-extrabold', labelClass)}
        >
          {target}
        </text>
        {hole('home', home, 2)}
        {lanes.map((lane, index) => {
          if (lane.score < target) return null;
          /* Under the pegs, so the peg's own ink edge stays on top. */
          const peg = archLane(s, lane.score, index, n, target);
          return (
            <circle
              key={`home-${String(index)}`}
              cx={peg.x}
              cy={peg.y}
              r={s.peg + 4}
              fill="none"
              className={BACK[lane.tone]}
              strokeWidth={2.5}
              data-home={lane.tone}
            />
          );
        })}
        {lanes.map((lane, index) => {
          const back = archLane(s, lane.back, index, n, target);
          const front = archLane(s, lane.score, index, n, target);
          return (
            <g key={`pegs-${String(index)}`} data-lane={lane.tone}>
              <circle
                cx={back.x}
                cy={back.y}
                r={s.peg - 1}
                fill="none"
                className={BACK[lane.tone]}
                strokeWidth={2}
              />
              <circle
                cx={front.x}
                cy={front.y}
                r={s.peg}
                className={FRONT[lane.tone]}
                strokeWidth={2}
              />
            </g>
          );
        })}
      </svg>
      {/* The playing surface, inside the arch and open at its foot, ranged
          from the left leg so its fixed width sits centered between the
          rails. */}
      <div
        className={cx(
          'absolute right-0 bottom-0 left-0 flex justify-start',
          FIELD_INSET[size][fieldLanes(n)],
        )}
      >
        {children}
      </div>
    </div>
  );
}
